import React, { useEffect } from 'react';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import type { AiUserAgent } from 'components/framework/models';
import classnames from 'classnames';
import AiTemplateController, { agentTypeNames } from './AiTemplateController';
import styles from './AiTemplatesModal.module.css';

interface DetailTemplateModalProps {
  template: AiUserAgent;
}

const DetailTemplateModal: React.FC<DetailTemplateModalProps> = ({
  template,
}) => {
  const controller = useController(AiTemplateController);

  // Set all anchor elements to open in a new page in the best practices section
  useEffect(() => {
    const bestPracticesDiv = document.getElementById('best-practices');
    if (bestPracticesDiv) {
      const anchors = bestPracticesDiv.getElementsByTagName('a');
      for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    }
  }, []);

  const { name, shortDescription, longDescription, guideDescription } =
    template;
  const categories = template.categories ?? [];

  const templateButton = (
    <aha-button
      style={{
        width: '188px',
        cursor: 'pointer',
        marginRight: '23px',
        fontSize: '17px',
      }}
      size='large'
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();

        controller.actionPlaceTemplate(
          template,
          'AI library details page',
          'More prompts'
        );
      }}
      kind='primary'
    >
      {controller.props.placeTemplate ? 'Use' : 'Add'}{' '}
      {agentTypeNames[template.agentType]}
    </aha-button>
  );

  return (
    <div className={styles.previewModal}>
      <div className={styles.previewHeader}>
        <div
          className={styles.previewHeaderButton}
          onClick={() => controller.actionSetCurrentTemplate(undefined)}
        >
          <aha-icon icon='far fa-left-long' style={{ marginRight: '5px' }} />
          Back to AI prompt library
        </div>
        <aha-icon
          class={styles.closeButton}
          icon='far fa-close'
          onClick={() => controller.actionAbortChooseTemplate()}
        />
      </div>
      <div className={styles.previewContent}>
        <div className={styles.previewDetail}>
          <div className={styles.detailLeft}>
            <div className={styles.templateCategory}>
              {categories.join(', ')}
              {
                <span className={styles.templateTileTag}>
                  {agentTypeNames[template.agentType]}
                </span>
              }
            </div>
            <div className={styles.templateName}>{name}</div>
            <div
              className={styles.templateCaption}
              // variable name indicates HTML; setter is responsbile for safety
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: shortDescription?.htmlBody ?? '',
              }}
            />
            <div>{templateButton}</div>
          </div>
          <div className={classnames(styles.detailRight)}>
            <img src={template.thumbnail || undefined} />
          </div>
        </div>
        <div className={styles.previewHowTo}>
          <div
            id='best-practices'
            className={classnames('aha-editor-field', styles.bestPractices)}
            // variable name indicates HTML; setter is responsbile for safety
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: longDescription?.htmlBody ?? '',
            }}
          />
          <div
            id='best-practices'
            className={classnames('aha-editor-field', styles.bestPractices)}
            // variable name indicates HTML; setter is responsbile for safety
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: guideDescription?.htmlBody ?? '',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default view(DetailTemplateModal);
